"use client";

import { useTranslations } from "next-intl";
import type { Review } from "@/lib/reviews/schema";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { StarRating } from "@/components/reviews/star-rating";
import { BADGE_ICONS } from "@/components/reviews/badge-icons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

type ReviewCardProps = {
  review: Review;
  dark?: boolean;
};

function formatReviewDate(iso: string): string {
  const tms = Date.parse(iso);
  if (!Number.isFinite(tms)) return "";
  return new Date(tms).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function ReviewCard({ review, dark = false }: ReviewCardProps) {
  const t = useTranslations("reviews");
  const isGiven = review.direction === "given";
  const party = isGiven ? review.counterparty : review.reviewer;
  const displayName =
    typeof party?.display_name === "string" && party.display_name.length > 0
      ? party.display_name
      : typeof party?.username === "string" && party.username.length > 0
        ? `@${party.username.replace(/^@/, "")}`
        : t("anonymousReviewer");

  const dateSource = review.order_placed_at ?? review.published_at ?? review.created_at;
  const dateLabel =
    review.order_placed_at != null
      ? t("orderPlacedOn", { date: formatReviewDate(review.order_placed_at) })
      : formatReviewDate(dateSource);

  const metaLabel = isGiven ? t("youReviewed", { name: displayName }) : dateLabel;

  return (
    <article
      className={
        dark
          ? "space-y-3 rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4"
          : "space-y-3 rounded-2xl border bg-card p-4"
      }
    >
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={getAvatarUrl(party?.avatar_url) ?? undefined} alt={displayName} />
          <AvatarFallback>{displayName.slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className={cn("truncate text-sm font-bold", dark && "text-white")}>{displayName}</p>
          <p className={dark ? "text-xs text-neutral-500" : "text-xs text-muted-foreground"}>
            {metaLabel}
          </p>
        </div>
        <StarRating value={review.overall_score} readOnly size={14} />
      </div>

      {review.badges.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {review.badges.map((code) => (
            <span
              key={code}
              className={
                dark
                  ? "inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground"
                  : "inline-flex items-center gap-1 rounded-full border bg-muted/50 px-3 py-1 text-xs font-semibold"
              }
            >
              {BADGE_ICONS[code] ? <span aria-hidden>{BADGE_ICONS[code]}</span> : null}
              {t(`badges.${code}`)}
            </span>
          ))}
        </div>
      ) : null}

      {typeof review.body === "string" && review.body.trim().length > 0 ? (
        <p className={cn("text-sm leading-relaxed", dark && "text-white/90")}>{review.body.trim()}</p>
      ) : null}
    </article>
  );
}
